import { useState } from 'react';
import { clsx } from '../ds/clsx';
import { Banner, Eyebrow } from '../ds/components';
import { errorMessage } from '../lib/api';
import styles from './WorkspaceSwitcher.module.css';

export interface WorkspaceOption {
  organizationId: string;
  name: string;
  /** Second line ("Berater"), the role in that workspace. */
  subline: string;
  active: boolean;
}

export interface WorkspaceSwitcherProps {
  options: WorkspaceOption[];
  onSelect: (organizationId: string) => Promise<void> | void;
}

/** The "Workspace wechseln" popover of Shell 02: one row per membership, the active one highlighted. */
export function WorkspaceSwitcher({ options, onSelect }: WorkspaceSwitcherProps) {
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function select(organizationId: string) {
    setError(null);
    setPending(organizationId);
    try {
      await onSelect(organizationId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(null);
    }
  }

  return (
    <div className={styles.panel}>
      <Eyebrow as="div" className={styles.eyebrow}>
        Workspace wechseln
      </Eyebrow>
      <div role="list">
        {options.map((option) => (
          <button
            key={option.organizationId}
            type="button"
            role="listitem"
            className={clsx(styles.row, option.active && styles.rowActive)}
            aria-current={option.active ? 'true' : undefined}
            aria-busy={pending === option.organizationId || undefined}
            disabled={pending !== null && pending !== option.organizationId}
            onClick={() => (option.active ? undefined : void select(option.organizationId))}
          >
            <span className={styles.rowText}>
              <span className={styles.rowName}>{option.name}</span>
              <span className={styles.rowSub}>{option.subline}</span>
            </span>
            {option.active ? <span className={styles.rowState}>aktiv</span> : null}
          </button>
        ))}
      </div>
      {error ? (
        <div className={styles.error}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}
      <div className={styles.footnote}>Ungespeicherte Eingaben bleiben im jeweiligen Workspace erhalten.</div>
    </div>
  );
}
